import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Activity,
  Users,
  Target,
  TrendingUp,
  Heart,
  Zap,
  BookOpen,
  BarChart3,
  Award,
  Clock,
  Star,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';

const LBWMainLanding = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Brain Wellness Assessments',
      description: 'Evidence-based assessments including ADHD, anxiety, stress, memory, and mood evaluations',
      icon: <Brain className="w-8 h-8" />,
      path: '/assessments',
      color: 'from-[#E4EFFF]0 to-purple-600',
      items: ['ADHD Rating Scale', 'GAD-7 Anxiety', 'PSS-10 Stress', 'Memory Tests', 'Mood Evaluation']
    },
    {
      title: 'Expert Coaching',
      description: 'Connect with certified brain wellness coaches for personalized guidance',
      icon: <Users className="w-8 h-8" />,
      path: '/coaching',
      color: 'from-green-500 to-teal-600',
      items: ['Brain Performance', 'Nervous System', 'P&ID Consultation', 'Stress Management', 'ADHD Support']
    },
    {
      title: 'Progress Tracking',
      description: 'Monitor your brain fitness journey with detailed analytics and insights',
      icon: <TrendingUp className="w-8 h-8" />,
      path: '/lbw/progress',
      color: 'from-orange-500 to-red-600',
      items: ['Weekly Reports', 'Goal Setting', 'Improvement Metrics', 'Personalized Insights']
    },
    {
      title: 'P&ID Analysis',
      description: 'Advanced brain mapping and neurofeedback protocols',
      icon: <Activity className="w-8 h-8" />,
      path: '/lbw/pid',
      color: 'from-purple-500 to-pink-600',
      items: ['Brain Mapping', 'Wave Analysis', 'Neurofeedback', 'Custom Protocols']
    },
    {
      title: 'Educational Content',
      description: 'Learn about brain wellness with our comprehensive resource library',
      icon: <BookOpen className="w-8 h-8" />,
      path: '/lbw/content',
      color: 'from-cyan-500 to-blue-600',
      items: ['Video Tutorials', 'Articles', 'Research Papers', 'Webinars']
    },
    {
      title: 'Personal Dashboard',
      description: 'Your central hub for all brain wellness activities and data',
      icon: <BarChart3 className="w-8 h-8" />,
      path: '/lbw/dashboard',
      color: 'from-indigo-500 to-purple-600',
      items: ['Activity Overview', 'Upcoming Sessions', 'Recent Results', 'Recommendations']
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Active Users' },
    { number: '95%', label: 'Success Rate' },
    { number: '50+', label: 'Expert Coaches' },
    { number: '24/7', label: 'Support Available' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'ADHD Patient',
      content: 'The assessments and coaching have transformed my ability to focus and manage my symptoms.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Stress Management Client',
      content: 'The neurofeedback sessions have significantly reduced my anxiety levels.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      role: 'Memory Enhancement',
      content: 'Ive seen remarkable improvements in my memory and cognitive function.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E4EFFF] via-white to-purple-50">
      <NavBar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-[#E4EFFF]0 to-purple-600 rounded-2xl">
                <Brain className="w-16 h-16 text-white" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Limitless Brain Wellness
              <span className="block text-3xl md:text-4xl mt-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Transform Your Mind, Elevate Your Life
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Comprehensive brain fitness platform with evidence-based assessments,
              expert coaching, and personalized neurofeedback protocols
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate('/assessments')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Start Free Assessment
              </button>
              <button
                onClick={() => navigate('/coaching')}
                className="px-8 py-4 bg-white text-gray-800 rounded-xl font-semibold text-lg border-2 border-gray-200 hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Find Your Coach
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-gray-600 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Complete Brain Wellness Suite
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for optimal brain health and cognitive performance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer group"
                onClick={() => navigate(feature.path)}
              >
                <div className={`h-2 bg-gradient-to-r ${feature.color}`}></div>
                <div className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {feature.description}
                  </p>
                  <div className="space-y-2 mb-6">
                    {feature.items.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-[#323956]" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#323956] font-semibold group-hover:gap-4 transition-all">
                    <span>Explore {feature.title}</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your Journey to Brain Wellness
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A simple, science-backed process to optimize your cognitive health
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Assess', desc: 'Take comprehensive brain wellness assessments', icon: <Brain /> },
              { step: '2', title: 'Analyze', desc: 'Get detailed insights and brain mapping', icon: <Activity /> },
              { step: '3', title: 'Connect', desc: 'Match with expert coaches and protocols', icon: <Users /> },
              { step: '4', title: 'Transform', desc: 'Track progress and achieve your goals', icon: <Sparkles /> }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#E4EFFF]0 to-purple-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                  <span className="text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands who have transformed their brain health
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Unlock Your Brain's Full Potential?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Start your journey today with our free assessment and personalized recommendations
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate('/assessments')}
              className="px-8 py-4 bg-white text-[#323956] rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate('/lbw/about')}
              className="px-8 py-4 bg-transparent text-white rounded-xl font-semibold text-lg border-2 border-white hover:bg-white hover:text-[#323956] transition-all duration-200"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LBWMainLanding;